/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useMemo, useState } from 'react'
import { MonkeyTable } from '@datasketch/monkeytab'
import { REGION_COLUMNS, REGION_ROWS } from '../../lib/scenario.ts'
import { downloadText, toCsv } from '../lib/download.ts'

/**
 * The tables demo (LANDING_PAGE.md §5.1).
 *
 * The real <MonkeyTable> from @datasketch/monkeytab, memory-backed — no server,
 * no adapter, fully client-side. What it has to show is the TYPE SYSTEM: these
 * are real typed columns, and the download is a real monkeytab project.
 */

type Row = Record<string, unknown>

/** toolbar (48) + header (44) + rows (44 each) + footer (36) + chrome. */
const GRID_HEIGHT = 48 + 44 + REGION_ROWS.length * 44 + 36 + 16

const CONFIG_JSON = JSON.stringify(
  { name: 'q3-by-region', columns: REGION_COLUMNS },
  null,
  2,
)

export function TableDemo() {
  const [rows, setRows] = useState<Row[]>(() => REGION_ROWS.map((r) => ({ ...r })))
  const [touched, setTouched] = useState(false)

  const columns = useMemo(() => REGION_COLUMNS, [])

  const handleChange = useCallback((next: Row[]) => {
    setRows(next)
    setTouched(true)
  }, [])

  const downloadCsv = useCallback(() => {
    downloadText('rows.csv', toCsv(columns, rows), 'text/csv')
  }, [columns, rows])

  const downloadConfig = useCallback(() => {
    downloadText('monkeytab.config.json', CONFIG_JSON, 'application/json')
  }, [])

  return (
    <div>
      <div className='scroll-x rounded-xl border border-[var(--edge)] bg-[var(--surface-raised)]'>
        <div className='min-w-[720px]'>
          {
            /* Explicit height. Omitting it makes the grid claim 100% of its
               container (≈600px of blank space under four rows); `height="auto"`
               computes ~half a row short and clips the last record. This fits
               toolbar + header + the seeded rows + footer exactly — revisit if
               the seed row count changes. */
          }
          <MonkeyTable
            columns={columns as never}
            rows={rows as never}
            onChange={handleChange as never}
            height={GRID_HEIGHT}
            editable
          />
        </div>
      </div>

      <div className='mt-4 flex flex-wrap items-center gap-3'>
        <button
          type='button'
          onClick={downloadCsv}
          className='rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--brand-ink)] hover:brightness-105'
        >
          ↓ Download rows.csv
        </button>
        <button
          type='button'
          onClick={downloadConfig}
          className='rounded-full border border-[var(--edge)] px-4 py-2 text-sm font-semibold hover:bg-[var(--surface-sunken)]'
        >
          ↓ monkeytab.config.json
        </button>
        <p className='text-sm text-[var(--ink-muted)]'>
          {touched
            ? 'Your edits are in that CSV. Open it anywhere.'
            : 'Edit a cell first, then download — the changes come with it.'}
        </p>
      </div>

      <p className='mt-3 text-xs text-[var(--ink-muted)]'>
        Those two files <em>are</em> a monkeytab project — the same pair{' '}
        <code className='font-mono'>monkeytab scaffold</code>{' '}
        writes. Nothing here is a page-specific export format.
      </p>
    </div>
  )
}
